import { Node } from 'reactflow';
import { NodeData, QAPair } from '../types';

/**
 * Represents a group of sibling nodes
 */
export interface SiblingGroup {
  id: string;
  parentId: string | undefined;
  depth: string;
  siblings: string[]; // Node IDs
  label: string;
}

/**
 * Identify groups of sibling nodes that should be visually grouped
 * Siblings are nodes with the same parent and same depth level
 */
export function identifySiblingGroups(qaPairs: QAPair[]): SiblingGroup[] {
  const groups: SiblingGroup[] = [];

  // Group nodes by their parent
  const nodesByParent = new Map<string, QAPair[]>();

  for (const pair of qaPairs) {
    const parentKey = pair.parentId || 'ROOT';
    if (!nodesByParent.has(parentKey)) {
      nodesByParent.set(parentKey, []);
    }
    nodesByParent.get(parentKey)!.push(pair);
  }

  // Create groups for each parent's children if there are 2+ siblings
  for (const [parentKey, children] of nodesByParent.entries()) {
    if (children.length >= 2) {
      // Further group by depth level
      const byDepth = new Map<string, QAPair[]>();
      for (const child of children) {
        const depth = child.question.depth;
        if (!byDepth.has(depth)) {
          byDepth.set(depth, []);
        }
        byDepth.get(depth)!.push(child);
      }

      // Create a group for each depth level with 2+ siblings
      for (const [depth, depthChildren] of byDepth.entries()) {
        if (depthChildren.length >= 2) {
          const parentId = parentKey === 'ROOT' ? undefined : parentKey;
          const siblingIds = depthChildren.map(c => c.question.id);

          // Generate group label
          const label = generateGroupLabel(depthChildren);

          groups.push({
            id: `group-${parentKey}-${depth}`,
            parentId,
            depth,
            siblings: siblingIds,
            label,
          });
        }
      }
    }
  }

  return groups;
}

/**
 * Generate a descriptive label for a sibling group
 */
function generateGroupLabel(siblings: QAPair[]): string {
  if (siblings.length === 0) return '';

  // Extract question type from first sibling
  const firstId = siblings[0].question.id;
  const parts = firstId.split('.');

  // For Root.Type.# format, use the Type
  if (parts[0] === 'Root' && parts.length >= 2) {
    const type = parts[1];
    return `${type} Questions`;
  }

  // For deeper levels, use the last type before the number
  const typeIndex = parts.findIndex(p => /^\d+$/.test(p)) - 1;
  if (typeIndex >= 0 && typeIndex < parts.length) {
    const type = parts[typeIndex];
    return `${type} Questions`;
  }

  return 'Related Questions';
}

/**
 * Get styling for group background based on depth
 */
export function getGroupStyle(depth: string) {
  // Very subtle backgrounds that don't interfere with node visibility
  const depthStyles: Record<string, any> = {
    L1: {
      backgroundColor: 'rgba(59, 130, 246, 0.03)', // Very light blue
      border: '1px dashed rgba(59, 130, 246, 0.2)',
    },
    L2: {
      backgroundColor: 'rgba(16, 185, 129, 0.03)', // Very light green
      border: '1px dashed rgba(16, 185, 129, 0.2)',
    },
    L3: {
      backgroundColor: 'rgba(139, 92, 246, 0.03)', // Very light purple
      border: '1px dashed rgba(139, 92, 246, 0.2)',
    },
    L4: {
      backgroundColor: 'rgba(245, 158, 11, 0.03)', // Very light amber
      border: '1px dashed rgba(245, 158, 11, 0.2)',
    },
    L5: {
      backgroundColor: 'rgba(236, 72, 153, 0.03)', // Very light pink
      border: '1px dashed rgba(236, 72, 153, 0.2)',
    },
  };

  return depthStyles[depth] || {
    backgroundColor: 'rgba(107, 114, 128, 0.03)',
    border: '1px dashed rgba(107, 114, 128, 0.2)',
  };
}

/**
 * Calculate bounding box for a group of sibling nodes
 */
function calculateGroupBounds(
  siblings: Node<NodeData>[],
  padding: number = 30
): { x: number; y: number; width: number; height: number } {
  if (siblings.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  // Find min/max coordinates
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const nodeWidth = 250; // Standard node width
  const nodeHeight = 150; // Standard node height

  for (const node of siblings) {
    const x = node.position.x;
    const y = node.position.y;

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + nodeWidth);
    maxY = Math.max(maxY, y + nodeHeight);
  }

  return {
    x: minX - padding,
    y: minY - padding,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2,
  };
}

/**
 * Create ReactFlow group background nodes for sibling groups
 * These are positioned after layout calculation
 */
export function createGroupBackgrounds(
  groups: SiblingGroup[],
  layoutedNodes: Node<NodeData>[]
): Node[] {
  const groupBackgrounds: Node[] = [];

  // Create a map for quick node lookup
  const nodeMap = new Map(layoutedNodes.map(n => [n.id, n]));

  for (const group of groups) {
    // Get the actual sibling nodes with positions
    const siblingNodes = group.siblings
      .map(id => nodeMap.get(id))
      .filter((n): n is Node<NodeData> => n !== undefined);

    if (siblingNodes.length < 2) {
      continue;
    }

    // Calculate bounding box for this group
    const bounds = calculateGroupBounds(siblingNodes);

    // Create a background node
    groupBackgrounds.push({
      id: group.id,
      type: 'default',
      position: { x: bounds.x, y: bounds.y },
      data: {
        label: group.label,
      },
      style: {
        ...getGroupStyle(group.depth),
        width: bounds.width,
        height: bounds.height,
        borderRadius: '12px',
        zIndex: -1, // Behind QA nodes
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: '10px',
        fontSize: '11px',
        fontWeight: 'bold',
        color: 'rgba(0, 0, 0, 0.3)',
      },
      draggable: false,
      selectable: false,
    });
  }

  return groupBackgrounds;
}

/**
 * No longer needed - we're not using parent nodes
 * Keeping for backwards compatibility
 */
export function assignNodesToGroups(
  nodes: Node<NodeData>[],
  groups: SiblingGroup[]
): Node<NodeData>[] {
  // Simply return nodes as-is
  return nodes;
}
